import {
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Bot } from "lucide-react";
import { toast } from "react-toastify";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { ZoomCallAiStatusData } from "@/modules/zoom-calls/types";
import { useGenerateZoomCallAiStatus } from "@/modules/zoom-calls/hooks/use-generate-zoom-call-ai-status.hook";

interface Props {
  zoomCallUid: string;
  aiStatus: ZoomCallAiStatusData | null;
  variant?: "compact" | "full";
}

const ZoomCallAiStatusButton = ({
  zoomCallUid,
  aiStatus,
  variant = "full",
}: Props) => {
  const theme = useTheme();
  const { generate, isLoading } = useGenerateZoomCallAiStatus(zoomCallUid);

  const status = aiStatus?.status ?? null;

  if (status === "completed") return null;

  const isProcessing =
    status === "pending" || status === "processing" || isLoading;

  return (
    <PermissionGuard
      permissions={[
        {
          name: PermissionsEnum.ZOOM_CALL_AI_STATUS,
          level: PermissionLevel.WRITE,
        },
      ]}
    >
      <Stack direction="column" spacing={0.5} alignItems="flex-start">
        <Button
          size={variant === "compact" ? "small" : "medium"}
          variant="outlined"
          disabled={isProcessing || !zoomCallUid}
          startIcon={
            isProcessing ? (
              <CircularProgress size={14} />
            ) : (
              <Bot
                size={16}
                color={theme.nawy.colors.semantic.text.accent}
              />
            )
          }
          onClick={(e) => {
            e.stopPropagation();
            generate(undefined, {
              onError: (err: any) => {
                const msg =
                  err?.response?.data?.message ??
                  "AI generation failed — try again";
                toast.error(msg, { position: "bottom-left" });
              },
              onSuccess: () => {
                toast.success("AI status generated", { position: "bottom-left" });
              },
            } as any);
          }}
          sx={{
            textTransform: "none",
            borderColor: theme.nawy.colors.semantic.border.secondary,
            color: theme.nawy.colors.semantic.text.primary,
            ...theme.nawy.typography.english.variants.body.sm.semibold,
          }}
        >
          {isProcessing ? "Generating…" : "Generate AI status"}
        </Button>
        {status === "failed" && !isLoading && (
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.xs.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            Previous attempt failed — try again.
          </Typography>
        )}
      </Stack>
    </PermissionGuard>
  );
};

export default ZoomCallAiStatusButton;
